import { useState, useCallback, useRef, useEffect } from "react";
import { documentReviewService } from "@/data/document-review-polygons";
import type { ReviewPolygon } from "@/types/document-review";

export function useDocumentPolygons(documentId: string | undefined) {
    const [polygons, setPolygons] = useState<ReviewPolygon[]>([]);
    const [selectedPolygon, setSelectedPolygon] = useState<ReviewPolygon | null>(null);
    const isCreatingRef = useRef<boolean>(false);
    const createTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Load polygons on mount and when documentId changes
    useEffect(() => {
        if (!documentId) return;
        const existing = documentReviewService.get(documentId);
        setPolygons(existing.polygons);
    }, [documentId]);

    // Create a new polygon with anti-duplication lock
    const createPolygon = useCallback((
        partial: Omit<ReviewPolygon, "id" | "createdAt" | "updatedAt">
    ) => {
        if (!documentId) return;
        
        // LOCK: Prevent multiple simultaneous creations
        if (isCreatingRef.current) {
            console.warn('[useDocumentPolygons] Creation locked, ignoring duplicate call');
            return;
        }

        // Set lock
        isCreatingRef.current = true;
        
        // Clear any existing timeout
        if (createTimeoutRef.current) {
            clearTimeout(createTimeoutRef.current);
        }

        try {
            // Create the polygon in the service (which adds ID and timestamps)
            const created = documentReviewService.addPolygon(documentId, partial);
            
            // Update state
            setPolygons((prev) => {
                // Double-check it's not already in the array (extra safety)
                if (prev.some(p => p.id === created.id)) {
                    console.warn('[useDocumentPolygons] Polygon already exists, skipping');
                    return prev;
                }
                return [...prev, created];
            });
            
            // Set as selected
            setSelectedPolygon(created);
            
            console.log('[useDocumentPolygons] Polygon created:', created.id);
        } catch (error) {
            console.error('[useDocumentPolygons] Error creating polygon:', error);
        } finally {
            // Release lock after a delay to prevent rapid-fire calls
            createTimeoutRef.current = setTimeout(() => {
                isCreatingRef.current = false;
            }, 200);
        }
    }, [documentId]);

    // Update an existing polygon
    const updatePolygon = useCallback((
        polygonId: string,
        updates: Partial<ReviewPolygon>
    ) => {
        if (!documentId) return;
        
        const updated = documentReviewService.updatePolygon(documentId, polygonId, updates);
        if (updated) {
            setPolygons((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
            
            // Update selected polygon if it's the one being updated
            if (selectedPolygon?.id === polygonId) {
                setSelectedPolygon(updated);
            }
        }
    }, [documentId, selectedPolygon]);

    // Delete a polygon
    const deletePolygon = useCallback((polygonId: string) => {
        if (!documentId) return;
        
        const success = documentReviewService.removePolygon(documentId, polygonId);
        if (success) {
            setPolygons((prev) => prev.filter((p) => p.id !== polygonId));
            
            // Clear selection if the deleted polygon was selected
            if (selectedPolygon?.id === polygonId) {
                setSelectedPolygon(null);
            }
        }
    }, [documentId, selectedPolygon]);

    // Select a polygon
    const selectPolygon = useCallback((polygonId: string | null) => {
        if (!polygonId) {
            setSelectedPolygon(null);
            return;
        }
        
        const found = polygons.find((p) => p.id === polygonId);
        setSelectedPolygon(found || null);
    }, [polygons]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (createTimeoutRef.current) {
                clearTimeout(createTimeoutRef.current);
            }
        };
    }, []);

    return {
        polygons,
        selectedPolygon,
        createPolygon,
        updatePolygon,
        deletePolygon,
        selectPolygon,
        setSelectedPolygon,
    };
}

